import React, { useContext, useState } from 'react';
import {
  Button,
  Column,
  FlexBox,
  FormError,
  FormField,
  FormInputGroup,
  ModalDialog,
  ModalDialogBody,
  ModalDialogButtons,
  ModalDialogCloseButton,
  ModalDialogContent,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogNav,
  ModalDialogTitle,
  SelectionSet,
  SelectionSetInput,
  SelectionSetLabel,
  Span,
  StandardForm,
  TextInput,
  Typography,
} from '@vp/swan';
import { ReplaceTokens, useTranslations } from '@vp/om-translations';
import { useQuery, useQueryClient } from 'react-query';
import isEmpty from 'lodash/isEmpty';
import { OrderDetailsContext } from '../../contexts/OrderDetailsContext';
import useSiteData from '../../../hooks/useSiteData';
import { EmbeddableLinkCreator } from '../../shared/EmbeddableLinkCreator';
import {
  fixPhoneNumberFormat,
  formatPhoneNumberForDisplay,
} from '../../../util/phoneNumberValidation';
import {
  deleteSubscription,
  isSaveButtonEnabledForSmsModal,
  makeAlertData,
  updateSubscription,
} from './smsUtils';
import { getSubscription } from '../../../apis/omniApi';
import { BasicPreloader } from '../../shared/BasicPreloader';
import { SourcePageContext } from '../../contexts/SourcePageContext';
import { GET_SMS_SUBSCRIPTION } from '../../../constants/reactQueryKeys';
import SmsSubscription from '../../../types/sms/SmsSubscription';
import { getShopperId } from '../../../util/auth';

type SmsSubscriptionModalProps = {
  updateAlert: (arg: boolean, message: string) => void;
};

const SmsSubscriptionModal = (props: SmsSubscriptionModalProps) => {
  const { updateAlert } = props;

  const {
    tenantEnvironment,
    order,
    isSmsSubscriptionModalOpen,
    setIsSmsSubscriptionModalOpen,
  } = useContext(OrderDetailsContext);

  const { isConfirmationPage: isOrderConfirmation } =
    useContext(SourcePageContext);

  const [smsManagementMessages, sharedActionMessages] = useTranslations([
    'smsManagementMessages',
    'sharedActionMessages',
  ]);
  const { externalAppNavigation } = useSiteData();
  const shopperId = order?.shopperId || getShopperId();

  const [optInCheckboxValue, setOptInCheckboxValue] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState(false);
  const [subscriptionData, setSubscriptionData] =
    useState<SmsSubscription | null>(null);
  const orderNumber = order?.orderNumber || null;
  // we use shippingAddress phone number because the field is optional on the billing address
  const [phoneNumberInput, setPhoneNumberInput] = useState(
    order?.shippingAddress?.phone ?? ''
  );
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);

  const queryClient = useQueryClient();

  // Fetch the subscription data for this order
  const { isLoading: isSubscriptionLoading } = useQuery(
    [GET_SMS_SUBSCRIPTION],
    async () =>
      getSubscription(tenantEnvironment || null, orderNumber, shopperId),
    {
      onSuccess: data => {
        const subData = data as SmsSubscription;
        if (!subData?.phoneNumber && !subData?.orderNumber) {
          setSubscriptionData(null);
        } else {
          setSubscriptionData(subData);
          setPhoneNumberInput(subData?.phoneNumber || '');
          setOptInCheckboxValue(!!subData);
        }
      },
      onError: () => {
        // TODO - revisit how we want to handle errors.
        setSubscriptionData(null);
      },
    }
  );

  if (
    !order ||
    !tenantEnvironment ||
    !isSmsSubscriptionModalOpen ||
    !setIsSmsSubscriptionModalOpen
  ) {
    return null;
  }

  const onPhoneNumberChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const phoneNumber = event.target.value;

    try {
      setPhoneNumberError(false);
      fixPhoneNumberFormat(phoneNumber, true);
      setPhoneNumberInput(phoneNumber);
    } catch (error) {
      setPhoneNumberError(true);
      setPhoneNumberInput(phoneNumber);
    }
  };

  /**
   * Use message from update response. Append `manage preferences message` if it is an order confirmation page.
   * @param alertMessage
   */
  const getTranslatedAlertMessage = (alertMessage: string) => {
    const translatedAlertMessage = smsManagementMessages[alertMessage];
    return isOrderConfirmation
      ? `${translatedAlertMessage} ${smsManagementMessages.toManagePreferences}`
      : translatedAlertMessage;
  };

  /**
   * Update subscription when the 'save' button is clicked. After we save / delete the subscription, re-fetch the updated subscription
   */
  const onUpdateSubscriptionClickHandler = async () => {
    setIsLoadingUpdate(true);
    const { alertMessage, error } = optInCheckboxValue
      ? await updateSubscription(
          orderNumber,
          phoneNumberInput,
          shopperId,
          tenantEnvironment,
          !subscriptionData
        )
      : await deleteSubscription(orderNumber, tenantEnvironment);

    makeAlertData(error, getTranslatedAlertMessage(alertMessage), updateAlert);
    // re-fetch updated subscription
    await queryClient.invalidateQueries({ queryKey: [GET_SMS_SUBSCRIPTION] });
    // close modal after save/delete (since preloader is rendered as part of the modal)
    setIsSmsSubscriptionModalOpen(false);
    setIsLoadingUpdate(false);
  };

  // setup for privacy link
  const navToPrivacy = isEmpty(externalAppNavigation.smsPrivacy)
    ? externalAppNavigation.customerCareContact
    : externalAppNavigation.smsPrivacy;
  const navToTerms = isEmpty(externalAppNavigation.smsTermsAndConditions)
    ? externalAppNavigation.customerCareContact
    : externalAppNavigation.smsTermsAndConditions;
  const PrivacyLink = EmbeddableLinkCreator({
    href: navToPrivacy,
    tabIndex: 0,
  });
  const TermsLink = EmbeddableLinkCreator({
    href: navToTerms,
    tabIndex: 0,
  });

  // modal styling
  const modalVariant = isOrderConfirmation ? 'standard' : 'panel-right';
  const modalTitle = isOrderConfirmation
    ? smsManagementMessages.optInModalTitle
    : smsManagementMessages.manageAlertsTitle;

  // While the subscription data is being fetched, show a preloader
  if (isSubscriptionLoading || isLoadingUpdate) {
    return <BasicPreloader isCentered my={9} />;
  }

  const modalContent = (
    <>
      <ModalDialogBody>
        {!isOrderConfirmation && (
          <Typography fontSkin="body-small">
            {smsManagementMessages.optInInstructions}
          </Typography>
        )}
        <SelectionSet
          mt={3}
          selectedValues={{ optInCheckBoxName: optInCheckboxValue }}
          skin="simple-column"
          variant="multi-select"
          id="optInPrompt"
          aria-labelledby="optInPrompt"
          role="group"
        >
          <SelectionSetInput
            value="optInCheckBoxName"
            aria-required="true"
            onChange={event => setOptInCheckboxValue(event.target.checked)}
          >
            <SelectionSetLabel>
              <Span fontSize="small">
                {smsManagementMessages.optInCheckbox}
              </Span>
            </SelectionSetLabel>
          </SelectionSetInput>
        </SelectionSet>
        <Column span={8} spanXs={10} offset={1} offsetSm={1}>
          <StandardForm>
            <FormField
              aria-label="subscription-phone-number"
              id="subscription-phone-number"
            >
              <FormInputGroup>
                <TextInput
                  data-testId="phone-number-input"
                  placeholder={smsManagementMessages.phoneNumberInputBox}
                  value={formatPhoneNumberForDisplay(phoneNumberInput)}
                  onChange={onPhoneNumberChanged}
                  aria-invalid={phoneNumberError}
                  aria-errormessage="invalid-phone-number"
                />
                {phoneNumberError && (
                  <FormError id="invalid-phone-number" role="status">
                    {smsManagementMessages.invalidPhoneNumber}
                  </FormError>
                )}
              </FormInputGroup>
            </FormField>
          </StandardForm>
          <Typography fontSkin="footnote" textColor="subtle">
            <ReplaceTokens
              tokenizedString={smsManagementMessages.footer}
              substitutions={{
                PrivacyLink,
                TermsLink,
              }}
            />
          </Typography>
        </Column>
      </ModalDialogBody>
      <ModalDialogFooter pinned>
        <FlexBox flexDirection="row" justifyContent="flex-end">
          <ModalDialogButtons>
            <Button
              skin="secondary"
              onClick={() => {
                setIsSmsSubscriptionModalOpen(false);
              }}
            >
              {sharedActionMessages.close}
            </Button>
            <Button
              skin="primary"
              onClick={() => onUpdateSubscriptionClickHandler()}
              disabled={
                !isSaveButtonEnabledForSmsModal(
                  subscriptionData || null,
                  phoneNumberInput,
                  phoneNumberError,
                  optInCheckboxValue,
                  isOrderConfirmation
                )
              }
            >
              {smsManagementMessages.optInConfirm}
            </Button>
          </ModalDialogButtons>
        </FlexBox>
      </ModalDialogFooter>
    </>
  );

  return (
    <ModalDialog
      isOpen={isSmsSubscriptionModalOpen}
      variant={modalVariant}
      bodyWidth="capped"
      onRequestDismiss={() => {
        setIsSmsSubscriptionModalOpen(false);
      }}
      onlyRenderWhenOpen
    >
      <ModalDialogContent>
        <ModalDialogNav>
          <ModalDialogCloseButton accessibleText={sharedActionMessages.close} />
        </ModalDialogNav>
        <ModalDialogHeader>
          <ModalDialogTitle>{modalTitle}</ModalDialogTitle>
        </ModalDialogHeader>
        {modalContent}
      </ModalDialogContent>
    </ModalDialog>
  );
};

export default SmsSubscriptionModal;
